import React from 'react';
import { API_BASE } from '../api';

const CORRECT_HEADLINES = [
  'Correct!',
  'Nailed it!',
  'Exactly right!',
  'That one is yours.',
];

function QuestionEditorPanel({
  selectedQuestion,
  onFieldChange,
  onGradeCurrent,
  canGradeCurrent,
  isGrading,
  feedback = {},
}) {
  if (!selectedQuestion) {
    return <div className="empty-editor">Select a question to review its details.</div>;
  }

  const grading = selectedQuestion.grading;
  const hasGrading = grading !== undefined;
  const questionImageSrc = resolveQuestionImageSrc(selectedQuestion);
  const isCorrect = Boolean(grading?.correct);
  const rationale = (grading?.rationale || '').trim();
  const expectedAnswer = grading?.expectedAnswer;
  const hasExpectedAnswer = expectedAnswer != null && expectedAnswer !== '';
  const celebration = isCorrect
    ? buildCorrectFeedback(feedback, selectedQuestion.id)
    : null;

  return (
    <div className="editor">
      <div className="field">
        <label>Question</label>
        <div className="question-prompt-display">{selectedQuestion.question}</div>
        {questionImageSrc && (
          <img
            className="question-image-display"
            src={questionImageSrc}
            alt="Question figure"
            loading="lazy"
          />
        )}
      </div>
      <div className="field">
        <label>Answer</label>
        <textarea
          value={selectedQuestion.studentAnswer || ''}
          onChange={(e) => onFieldChange('studentAnswer', e.target.value)}
          rows={6}
          placeholder="Type your answer here..."
        />
        <button
          type="button"
          className="primary grade-current-btn"
          onClick={onGradeCurrent}
          disabled={isGrading || !canGradeCurrent}
        >
          {isGrading ? 'Grading…' : 'Grade my answer'}
        </button>
      </div>
      {hasGrading && (
        <div className="grading-result">
          <div
            key={`${selectedQuestion.id}-${selectedQuestion.gradingAnimationVersion || 0}`}
            className={`feedback-banner ${isCorrect ? 'correct' : 'incorrect'}`}
            role="status"
          >
            {isCorrect && (
              <span className="feedback-confetti" aria-hidden="true">
                {Array.from({ length: 8 }, (_, index) => (
                  <i key={index} style={{ '--particle': index }} />
                ))}
              </span>
            )}
            <span className="feedback-icon" aria-hidden="true">{isCorrect ? celebration.icon : '↻'}</span>
            <span className="feedback-text">
              <span className="feedback-headline">
                {isCorrect ? celebration.headline : 'Not quite yet — you can retry this one.'}
              </span>
              {isCorrect ? (
                celebration.subline && (
                  <span className="feedback-subline">{celebration.subline}</span>
                )
              ) : (
                <span className="feedback-subline">
                  Change your answer above and grade it again. Nothing is locked in.
                </span>
              )}
            </span>
          </div>

          {rationale && (
            <div className="field">
              <label>{isCorrect ? 'Why this works' : 'What to look at'}</label>
              <div className="feedback-rationale">{rationale}</div>
            </div>
          )}

          <div className="field">
            <label>Your answer</label>
            <div className="student-answer-display">{grading.studentAnswer || 'No answer provided'}</div>
          </div>

          {hasExpectedAnswer && (
            <details className="solution-details" open={!isCorrect && !rationale}>
              <summary>
                {isCorrect ? 'Compare with the worked solution' : 'Show the worked solution'}
              </summary>
              <div className="expected-answer-display">{expectedAnswer}</div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// Milestones first, so a streak or a finished skill outranks generic praise.
function buildCorrectFeedback({ correctCount = 0, streak = 0, skillTarget = 0 }, questionId) {
  const remaining = skillTarget - correctCount;

  if (skillTarget > 0 && remaining <= 0) {
    return {
      icon: '🎉',
      headline: 'Skill complete!',
      subline: `${correctCount} correct — you've finished this skill.`,
    };
  }

  const subline = remaining > 0
    ? `${remaining} more correct answer${remaining !== 1 ? 's' : ''} to complete this skill.`
    : '';

  if (streak >= 10) {
    return { icon: '🔥', headline: `Unstoppable — ${streak} in a row!`, subline };
  }
  if (streak >= 5) {
    return { icon: '🔥', headline: `On fire — ${streak} in a row!`, subline };
  }
  if (streak >= 3) {
    return { icon: '🔥', headline: `${streak} in a row!`, subline };
  }
  if (correctCount === 1) {
    return { icon: '✓', headline: 'First one down!', subline };
  }

  return {
    icon: '✓',
    headline: CORRECT_HEADLINES[hashToIndex(questionId, CORRECT_HEADLINES.length)],
    subline,
  };
}

// Keeps the headline stable for a given question instead of changing on re-render.
function hashToIndex(value, buckets) {
  const text = String(value || '');
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 100000;
  }
  return hash % buckets;
}

function resolveQuestionImageSrc(question) {
  const rawImage = (question?.imageQuestionUrl || question?.imageQuestion || '').trim();
  if (!rawImage) return '';
  if (/^(https?:\/\/|data:|blob:)/i.test(rawImage)) return rawImage;

  const normalizedPath = rawImage.startsWith('/') ? rawImage : `/${rawImage}`;
  const apiBase = (API_BASE || '').replace(/\/$/, '');

  if (normalizedPath.startsWith('/api/')) {
    return `${apiBase}${normalizedPath}`;
  }

  return `${apiBase}/api/question-images/${encodeURI(rawImage.replace(/^\/+/, ''))}`;
}

export default QuestionEditorPanel;
